import {
  User,
  Target,
  Zap,
  Shield,
  BarChart3,
  Users,
  Briefcase,
} from 'lucide-react'

export interface ProfileTheme {
  heroBg: string
  barColor: string
  cardBg: string
  cardBorder: string
  cardIconBg: string
  cardIconText: string
  cardText: string
}

export interface ProfileContent {
  title: string
  tagline: string
  summary: string
  theme: ProfileTheme
  icon: any
  color: string

  successRoles: string[]
  frictionRoles: string[]
  whyItMatters: string
  strengths: string[]
  growthAreas: string[]
  leanInto: string[]
  develop: string[]
  comparativeNote: string
}

// UNIFIED DARK THEME FOR ALL PROFILES
const DARK_THEME: ProfileTheme = {
  heroBg: 'bg-[#0B0F14]',
  barColor: '#E10600',
  cardBg: 'bg-[#111318]',
  cardBorder: 'border-[#2a2f3b]',
  cardIconBg: 'bg-[#E10600]/10',
  cardIconText: 'text-[#E10600]',
  cardText: 'text-[#F5F5F4]',
}

export const PROFILE_CONTENT: Record<string, ProfileContent> = {
  Hunter: {
    title: 'The Hunter',
    tagline: 'Built for the Front Lines',
    summary:
      'Based on your responses, you are most likely to thrive in high-activity, results-driven environments. You naturally gravitate toward competition, recover quickly from setbacks, and prefer roles where your effort directly determines your outcome. You are wired for pace and pressure.',
    color: 'rose',
    theme: DARK_THEME,
    icon: Target,
    successRoles: [
      'New business development and cold outreach',
      'High-volume, performance-based roles with uncapped earning potential',
      'Territory-based roles with autonomy and ownership',
      'Fast-paced environments where activity directly drives results',
    ],
    frictionRoles: [
      'Account management with long nurture cycles',
      'Support-heavy roles with limited autonomy',
      'Highly structured environments with rigid processes',
      'Team-based roles with shared credit and slow decision-making',
    ],
    whyItMatters:
      'In sales, rejection is constant. Your responses indicate a strong ability to absorb rejection without losing momentum — a trait consistently found in top performers. Your competitive drive and bias toward action mean you are likely to maintain high activity levels even on difficult days.',
    strengths: [
      'Recovers from rejection faster than most — maintains energy and focus after setbacks',
      'Self-motivated and internally driven — does not need external pressure to perform',
      'Naturally competitive — uses scoreboards and rankings as fuel',
      'Action-oriented — prefers doing over planning, which drives high contact rates',
    ],
    growthAreas: [
      'May rush through conversations without fully qualifying prospects',
      'Can deprioritize follow-up and relationship nurturing in favor of new leads',
      'May resist structured processes that feel like they slow momentum',
    ],
    leanInto: [
      'Your natural urgency and pace — this is your competitive edge in high-volume environments',
      'Your resilience — actively seek out the toughest challenges and hardest objections to sharpen your skills',
      'Your independence — take ownership of your results and treat your role like your own business',
    ],
    develop: [
      'Active listening during conversations — pause before responding to fully understand the prospect',
      'A simple follow-up system — even a basic tracker will significantly increase your close rate',
      'Patience with process — learning to trust a repeatable system will compound your results over time',
    ],
    comparativeNote:
      'Your responses indicate stronger competitiveness and action-orientation than candidates who prefer structured or support-focused roles.',
  },
  Closer: {
    title: 'The Closer',
    tagline: 'Influence Meets Drive',
    summary:
      'Based on your responses, you combine strong interpersonal skills with a genuine drive to win. You are most likely to succeed in roles that require building rapport quickly, reading people accurately, and guiding conversations toward a decision. You balance persistence with social intelligence.',
    color: 'amber',
    theme: DARK_THEME,
    icon: Zap,
    successRoles: [
      'Consultative sales with a relationship-driven process',
      'Presentations, demos, and face-to-face selling',
      'Roles requiring strong objection handling and negotiation',
      'Team lead positions combining selling with mentoring',
    ],
    frictionRoles: [
      'Pure cold outreach with no relationship component',
      'Highly transactional, volume-only environments',
      'Roles with rigid scripts and no room for personalization',
      'Back-office or operational positions with minimal client interaction',
    ],
    whyItMatters:
      'Your blend of drive and influence is particularly valuable in environments where the conversation matters as much as the activity. You are likely to convert at a higher rate than pure volume sellers because you naturally adapt your approach to each prospect. This combination is often found in top closers who consistently exceed targets.',
    strengths: [
      'Reads people and adjusts approach in real time — high social intelligence',
      'Persuasive without being pushy — builds genuine trust quickly',
      'Balances relationship-building with closing — does not lose sight of the goal',
      'Comfortable with objections — sees them as conversation, not conflict',
    ],
    growthAreas: [
      'May over-invest time in prospects who are unlikely to convert',
      'Can sometimes prioritize being liked over asking for the commitment',
      'May need to increase raw activity volume to match pure hunters',
    ],
    leanInto: [
      'Your ability to build trust fast — this is what separates good sellers from great ones',
      "Your natural persuasion skills — practice framing value in the prospect's own language",
      'Your adaptability — use your read on people to customize your approach on the fly',
    ],
    develop: [
      'Time management per interaction — set internal limits to avoid over-investing in low-probability prospects',
      'Comfort with direct asks — practice closing earlier in the conversation',
      'Activity discipline — pair your conversion skills with consistent daily volume targets',
    ],
    comparativeNote:
      'Your responses suggest a stronger blend of social influence and drive than candidates who lean purely toward competition or purely toward relationship-building.',
  },
  'Steady Performer': {
    title: 'The Steady Performer',
    tagline: 'Consistency Is the Advantage',
    summary:
      'Based on your responses, you are most likely to succeed in roles that reward reliability, follow-through, and long-term relationship building. You prefer clear systems and steady progress over high-risk, high-reward environments. Your consistency is a genuine competitive advantage in the right role.',
    color: 'blue',
    theme: DARK_THEME,
    icon: Shield,
    successRoles: [
      'Account management and client retention',
      'Repeat-engagement sales with established relationships',
      'Roles with clear daily processes and expectations',
      'Customer success and relationship management',
    ],
    frictionRoles: [
      'Pure cold outreach with high rejection rates',
      'High-volume, high-pressure environments',
      'Roles with no structure or defined process',
      'Competitive leaderboard-driven cultures',
    ],
    whyItMatters:
      'In many sales organizations, the steady performers are the backbone. While hunters generate new business, steady performers retain it. Your responses indicate strong follow-through and dependability — traits that build trust with customers over time and reduce churn. Your consistency means you are likely to show up and execute every day, which compounds into strong results.',
    strengths: [
      'Exceptionally reliable — follows through on every commitment without exception',
      'Detail-oriented — catches errors and ensures quality in every interaction',
      'Builds deep client trust — customers feel genuinely cared for',
      'Consistent daily execution — shows up and performs regardless of external conditions',
    ],
    growthAreas: [
      'May hesitate to push for the close when the prospect is on the fence',
      'Can be uncomfortable with ambiguity or rapidly changing priorities',
      'May avoid taking risks that could accelerate growth',
    ],
    leanInto: [
      'Your reliability — make it your brand. Customers and managers will trust you implicitly',
      'Your attention to detail — use it to catch opportunities others miss in follow-up',
      'Your consistency — commit to a daily activity number and never miss it',
    ],
    develop: [
      'Comfort with asking for the sale directly — practice specific closing phrases until they feel natural',
      'Flexibility when plans change — build a simple framework for adapting on the fly',
      'Willingness to stretch beyond your comfort zone — set one small risk goal per week',
    ],
    comparativeNote:
      'Your responses indicate stronger consistency and process-orientation than candidates who prefer fast-paced, unstructured environments.',
  },
  'At-Risk': {
    title: 'The Analyst',
    tagline: 'Precision Over Pressure',
    summary:
      'Based on your responses, your natural preferences lean toward careful analysis, structured environments, and lower-rejection work. This does not mean you lack talent — it means the specific demands of high-volume sales may not align with where you naturally perform best. Understanding this fit is valuable for both you and the organization.',
    color: 'slate',
    theme: DARK_THEME,
    icon: BarChart3,
    successRoles: [
      'Operations and process improvement',
      'Customer support and service roles',
      'Data analysis and reporting',
      'Administrative and coordination positions',
    ],
    frictionRoles: [
      'High-rejection outbound sales roles',
      'Commission-only compensation structures',
      'Unstructured, autonomous positions with ambiguous goals',
      'Fast-paced environments with constant pivoting',
    ],
    whyItMatters:
      'High-volume sales involves frequent rejection, ambiguity, and self-directed activity. Your responses suggest these conditions may feel draining rather than energizing. This is not a weakness; it is a fit signal. Many highly successful professionals thrive in roles that leverage analytical thinking, precision, and structured collaboration rather than high-pressure selling.',
    strengths: [
      'Strong analytical and critical thinking skills',
      'Values accuracy and thoroughness in work',
      'Collaborative and team-oriented — contributes to group harmony',
      'Careful decision-making — avoids costly mistakes',
    ],
    growthAreas: [
      'Building comfort with ambiguity and unstructured situations',
      'Developing resilience to frequent rejection or negative feedback',
      'Taking initiative without waiting for complete information',
    ],
    leanInto: [
      'Your analytical strengths — apply them to roles where precision creates value',
      'Your collaborative nature — seek team environments where your contributions are visible',
      'Your thoroughness — use it to excel in quality-focused positions',
    ],
    develop: [
      'Comfort with imperfect action — practice making decisions with 80% of the information',
      'Resilience exercises — gradually expose yourself to low-stakes rejection to build tolerance',
      'Self-advocacy — practice communicating your value and contributions proactively',
    ],
    comparativeNote:
      'Your responses suggest a preference for structure and analysis over the high-autonomy, high-rejection nature of sales-focused roles.',
  },
  'Review Manually': {
    title: 'The Hybrid',
    tagline: 'A Unique Combination',
    summary:
      'Your responses show a distinctive mix of traits that does not fit neatly into a single profile. This often indicates versatility — you may adapt your approach depending on the situation. A conversation with our team will help identify the specific role where your unique combination creates the most value.',
    color: 'violet',
    theme: DARK_THEME,
    icon: Users,
    successRoles: [
      'Roles requiring adaptability across different selling situations',
      'Hybrid positions combining sales with account management',
      'Team environments where diverse skills are valued',
      'Roles with variety in daily tasks and responsibilities',
    ],
    frictionRoles: [
      'Narrowly defined roles that only reward one skill',
      'Environments that penalize versatility',
      'Highly rigid structures with no room for adaptation',
      'Roles requiring extreme specialization in one area',
    ],
    whyItMatters:
      'Versatility can be a significant asset in the right environment. Your responses suggest you draw from multiple behavioral tendencies rather than relying on a single dominant style. In practice, this means you may perform well across different types of interactions, but may need to identify which specific behaviors to emphasize in a given role.',
    strengths: [
      'Adaptable — adjusts approach based on the situation',
      'Balanced perspective — sees multiple sides of a challenge',
      'Versatile skill set — can contribute in various capacities',
      'Open-minded — willing to try different approaches',
    ],
    growthAreas: [
      'Identifying and committing to a primary selling style',
      'Building depth in one area rather than breadth across many',
      'Developing a consistent daily routine that leverages your strongest traits',
    ],
    leanInto: [
      'Your adaptability — use it to handle diverse prospect types effectively',
      'Your balanced perspective — apply it to problem-solving in complex situations',
      'Your openness — be willing to experiment with different approaches to find your groove',
    ],
    develop: [
      'Self-awareness about which situations bring out your best performance',
      'A consistent daily process — even versatile sellers need a repeatable foundation',
      'Depth in your strongest area — identify your top trait and invest in developing it further',
    ],
    comparativeNote:
      'Your responses show a balanced distribution across traits, suggesting adaptability rather than a single dominant behavioral style.',
  },
}
